/**
 * /tracking/click-counts — click tracking counts dashboard (feature #167).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `ClickCountsPage`. The flat-file naming convention uses dots as path
 * separators, so `tracking.click-counts.tsx` maps to `/tracking/click-counts`.
 *
 * Accepts optional deep-links:
 *   ?offerId=off_123      — preselect the offer filter
 *   ?affiliateId=aff_456  — preselect the affiliate filter
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { ClickCountsPage } from "@/features/click-counts/ClickCountsPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/tracking/click-counts`.
// ---------------------------------------------------------------------------

interface ClickCountsSearch {
	offerId?: string;
	affiliateId?: string;
}

export const trackingClickCountsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/click-counts",
	validateSearch: (raw: Record<string, unknown>): ClickCountsSearch => {
		const out: ClickCountsSearch = {};
		if (typeof raw.offerId === "string" && raw.offerId.trim().length > 0) {
			out.offerId = raw.offerId.trim();
		}
		if (
			typeof raw.affiliateId === "string" &&
			raw.affiliateId.trim().length > 0
		) {
			out.affiliateId = raw.affiliateId.trim();
		}
		return out;
	},
	component: TrackingClickCountsRoutePage,
});

export default trackingClickCountsRoute;

// Wrapper reads the search params so the page can deep-link without prop
// drilling from the router call site.
export function TrackingClickCountsRoutePage() {
	const { offerId, affiliateId } = trackingClickCountsRoute.useSearch();
	return (
		<ClickCountsPage
			initialOfferId={offerId}
			initialAffiliateId={affiliateId}
		/>
	);
}

// Re-export for the wire-up cycle / root registry import.
export { ClickCountsPage } from "@/features/click-counts/ClickCountsPage";
